import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { VerwendungCardComponent } from '../../../../components/verwendung-card/verwendung-card.component';
import { TestModuleUsage } from '../../../../models/test-module.model';
import { VerwendungReiter, VerwendungStatus } from '../../../../models/verwendung.model';
import { TestModuleService } from '../../../../services/test-module.service';

const KEY = 'modules.form.verwendung.';

@Component({
  selector: 'app-module-usage',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, VerwendungCardComponent],
  template: `
    <app-verwendung-card
      testId="module-usage-card"
      [subtitle]="'${KEY}untertitel' | translate"
      [tabs]="tabs()"
      [status]="status()"
      (erneutVersuchen)="erneutLaden()"
    />
  `,
})
export class ModuleUsageComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly moduleService = inject(TestModuleService);
  private readonly translate = inject(TranslateService);

  readonly projectId = input.required<string>();
  readonly moduleId = input<string | null>(null);

  protected readonly status = signal<VerwendungStatus>('bereit');
  private readonly usage = signal<TestModuleUsage[]>([]);
  private readonly sprachwechsel = toSignal(this.translate.onLangChange, { initialValue: null });
  private laden: Subscription | null = null;

  protected readonly tabs = computed<VerwendungReiter[]>(() => {
    this.sprachwechsel();
    const projectId = this.projectId();
    const usage = this.usage();
    const text = (key: string): string => this.translate.instant(KEY + key);
    return [
      {
        id: 'module',
        label: text('module'),
        icon: 'content_copy',
        leerText: text('moduleLeer'),
        suchPlatzhalter: text('moduleSuchen'),
        eintraege: usage
          .filter((u) => u.kind === 'MODULE')
          .map((u) => ({
            id: u.id,
            key: u.key,
            titel: u.title,
            ziel: { commands: ['/projects', projectId, 'test-modules', u.id, 'edit'] },
          })),
      },
      {
        id: 'testfaelle',
        label: text('testfaelle'),
        icon: 'folder',
        leerText: text('testfaelleLeer'),
        suchPlatzhalter: text('testfaelleSuchen'),
        eintraege: usage
          .filter((u) => (u.kind ?? 'TEST_CASE') === 'TEST_CASE')
          .map((u) => ({
            id: u.id,
            key: u.key,
            titel: u.title,
            ziel: { commands: ['/projects', projectId, 'test-cases', u.id, 'edit'] },
          })),
      },
    ];
  });

  constructor() {
    effect(() => this.lade(this.projectId(), this.moduleId()));
  }

  protected erneutLaden(): void {
    this.lade(this.projectId(), this.moduleId());
  }

  private lade(projectId: string, moduleId: string | null): void {
    this.laden?.unsubscribe();
    this.usage.set([]);
    if (!moduleId) {
      this.status.set('bereit');
      return;
    }
    this.status.set('laedt');
    this.laden = this.moduleService
      .listUsage(projectId, moduleId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (usage) => {
          this.usage.set(usage);
          this.status.set('bereit');
        },
        error: () => this.status.set('fehler'),
      });
  }
}
